import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowUpRight,
  Globe2,
  Award,
  Compass,
  ShieldCheck,
  Cpu,
  Zap,
  Layers,
  Star,
  CheckCircle2,
  TrendingUp,
  Users,
  BarChart2,
  Settings,
  Wrench,
  Activity,
  Target,
  Sparkles,
} from 'lucide-react';
import { COMPANY_INFO } from '../data/simatechData';
import { useCms } from '../context/CmsContext';

interface WhyChooseUsProps {
  onOpenInquiry: (subject?: string) => void;
  onNavigate?: (page: any) => void;
  onOpenConnect?: () => void;
}

const getBenefitIcon = (iconName: string) => {
  switch (iconName) {
    case 'Globe2':
    case 'Globe':
      return Globe2;
    case 'Award':
      return Award;
    case 'Compass':
      return Compass;
    case 'ShieldCheck':
      return ShieldCheck;
    case 'Cpu':
      return Cpu;
    case 'Zap':
      return Zap;
    case 'Layers':
      return Layers;
    case 'Star':
      return Star;
    case 'CheckCircle2':
      return CheckCircle2;
    case 'TrendingUp':
      return TrendingUp;
    case 'Users':
      return Users;
    case 'BarChart2':
      return BarChart2;
    case 'Settings':
      return Settings;
    case 'Wrench':
      return Wrench;
    case 'Activity':
      return Activity;
    case 'Target':
      return Target;
    default:
      return ShieldCheck;
  }
};

export const WhyChooseUsSection: React.FC<WhyChooseUsProps> = ({
  onOpenInquiry,
  onNavigate,
  onOpenConnect,
}) => {
  const { siteData } = useCms();
  const brand = siteData?.brand || COMPANY_INFO;
  const homeData = siteData?.pages?.home;

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionBgColor = homeData?.whySectionBgColor || '#EFE8D8';
  const sectionBadge = homeData?.whySectionBadge || homeData?.whyBadge || 'WHY CHOOSE US';
  const sectionHeading = homeData?.whySectionHeading || homeData?.whyHeading || 'Supporting Growth on a Global Scale';
  const sectionDescription =
    homeData?.whySectionSubheading ||
    homeData?.whySubheading ||
    homeData?.whySectionDescription ||
    `We provide dependable and tailored turnkey automation services that help your business navigate technical challenges and maximize plant productivity. With over ${brand.experienceYears && brand.experienceYears !== '14+' && brand.experienceYears !== '18+' ? brand.experienceYears : '20+'} years of excellence of dedicated engineering and commissioning across ${brand.globalDestinations || '14+'} countries, Simatech delivers precision control panel fabrication and Siemens drive systems.`;
  const sectionCtaText = homeData?.whySectionCtaText || 'More About Us';
  const sectionImageUrl =
    homeData?.whySectionImageUrl ||
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80';

  const benefitCards =
    homeData?.whySectionCards && homeData.whySectionCards.length > 0
      ? homeData.whySectionCards
      : [
          {
            icon: 'Globe2',
            title: 'Global Commissioning',
            description: 'Projects commissioned across 14+ countries with dedicated on-site engineering support.',
          },
          {
            icon: 'Award',
            title: 'ISO 9001:2015 Certified',
            description: 'Quality-assured engineering, rigorous FAT protocols, and panel fabrication processes.',
          },
          {
            icon: 'Compass',
            title: 'Siemens Partner',
            description: 'Authorized Siemens Channel Partner with direct hardware procurement and warranty backing.',
          },
        ];

  // Dynamic typography & color overrides
  const headingFontFamily = homeData?.whyHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.whyHeadingFontSize;
  const headingFontWeight = homeData?.whyHeadingFontWeight || '300';
  const headingColor = homeData?.whyHeadingColor || '#06242a';

  const descFontFamily = homeData?.whyDescFontFamily || "'Google Sans Flex', sans-serif";
  const descFontSize = homeData?.whyDescFontSize;
  const descColor = homeData?.whyDescColor || '#4e6569';

  const ctaFontFamily = homeData?.whyCtaFontFamily;
  const ctaFontSize = homeData?.whyCtaFontSize;
  const ctaColor = homeData?.whyCtaColor || '#ffffff';
  const ctaBgColor = homeData?.whyCtaBgColor || '#1b6f7e';
  const ctaBorderColor = homeData?.whyCtaBorderColor || 'rgba(56, 201, 222, 0.3)';

  const cardTitleFontFamily = homeData?.whyCardTitleFontFamily || "'Jost', sans-serif";
  const cardTitleFontSize = homeData?.whyCardTitleFontSize;
  const cardTitleColor = homeData?.whyCardTitleColor || '#0d3f48';
  const cardDescColor = homeData?.whyCardDescColor || '#4e6569';

  const handleCtaClick = () => {
    const target = homeData?.whySectionCtaTarget || 'page:about';
    if (target.startsWith('modal:inquiry')) {
      onOpenInquiry('Global Automation Commissioning');
    } else if (target.startsWith('modal:connect')) {
      if (onOpenConnect) onOpenConnect();
      else onOpenInquiry('Global Automation Commissioning');
    } else if (target.startsWith('page:')) {
      const pageName = target.replace('page:', '');
      if (onNavigate) onNavigate(pageName);
      else window.location.hash = `#${pageName}`;
    } else if (target.startsWith('section:') || target.startsWith('#')) {
      const secId = target.replace('section:', '').replace('#', '');
      const el = document.getElementById(secId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else if (onNavigate) onNavigate('home');
    } else {
      if (onNavigate) onNavigate('about');
      else onOpenInquiry('Global Automation Commissioning');
    }
  };

  return (
    <section
      id="why-us"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden border-t border-[#1b6f7e]/15"
      style={{ backgroundColor: sectionBgColor }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Split: Text & Circular Experience Badge Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          
          {/* Left Column: Heading & Description */}
          <div
            className={`lg:col-span-7 flex flex-col items-start transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#06242a] text-[#38c9de] text-xs font-mono tracking-widest uppercase mb-4 border border-[#38c9de]/30 shadow-xs">
              <Sparkles className="w-3 h-3 text-[#38c9de] animate-pulse" />
              <span>{sectionBadge}</span>
            </div>

            <h2
              className="text-[22px] sm:text-[27px] md:text-[36px] font-light leading-[1.25] tracking-tight mb-4"
              style={{
                fontFamily: headingFontFamily,
                fontSize: headingFontSize || undefined,
                fontWeight: headingFontWeight,
                color: headingColor,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
            </h2>
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mb-6" />

            <div
              className="text-base sm:text-lg font-light leading-relaxed mb-8 prose prose-slate"
              style={{
                fontFamily: descFontFamily,
                fontSize: descFontSize || undefined,
                color: descColor,
              }}
              dangerouslySetInnerHTML={{ __html: sectionDescription }}
            />

            <button
              id="why-choose-us-cta"
              type="button"
              onClick={handleCtaClick}
              className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-wider uppercase transition-all shadow-sm group cursor-pointer"
              style={{
                fontFamily: ctaFontFamily || 'monospace',
                fontSize: ctaFontSize || undefined,
                color: ctaColor,
                backgroundColor: ctaBgColor,
                border: `1px solid ${ctaBorderColor}`,
              }}
            >
              <span>{sectionCtaText}</span>
              <ArrowUpRight className="w-4 h-4 text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>

          {/* Right Column: Experience Badge & Image Card */}
          <div
            className={`lg:col-span-5 flex items-center justify-center relative transition-all duration-700 delay-200 ease-out ${
              isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-8 scale-95'
            }`}
          >
            <div className="relative w-full max-w-sm group/frame">
              
              {/* Circular Experience Stamp Badge with Float / Tilt Effect */}
              <div className="absolute -top-8 -left-6 z-20 w-28 h-28 bg-[#0d3f48] p-2 border-4 border-white shadow-xl flex items-center justify-center text-white transition-transform duration-500 group-hover/frame:scale-110 group-hover/frame:-rotate-3 group-hover/frame:shadow-2xl">
                <div className="text-center">
                  <div className="text-2xl font-light text-[#30b6cb] leading-none">{brand.experienceYears && brand.experienceYears !== '14+' && brand.experienceYears !== '18+' ? brand.experienceYears : '20+'}</div>
                  <div className="text-[9px] font-normal uppercase tracking-wider text-[#d4ebef] mt-0.5">Years of</div>
                  <div className="text-[9px] font-normal uppercase tracking-wider text-[#d4ebef]">Excellence</div>
                </div>
              </div>

              {/* Facility Image Frame with Smooth Hover Zoom */}
              <div className="overflow-hidden shadow-2xl border-4 border-white aspect-[4/5] bg-[#0d3f48] transition-all duration-500 group-hover/frame:shadow-[0_30px_60px_rgba(13,63,72,0.3)]">
                <img
                  src={sectionImageUrl}
                  alt="Industrial Automation Facility"
                  width={480}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover/frame:scale-108"
                />
              </div>

            </div>
          </div>

        </div>

        {/* Feature Highlights Grid */}
        <div
          className={`border-t border-[#1b6f7e]/20 pt-10 pb-8 transition-all duration-700 delay-300 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Feature 1 */}
            <div className="flex items-center gap-3.5 p-2 rounded-xl transition-all duration-300 hover:bg-white/40 hover:translate-x-1 group">
              <div className="w-10 h-10 bg-[#eef7f9] text-[#1b7481] flex items-center justify-center group-hover:bg-[#1b7481] group-hover:text-white transition-colors duration-300 shadow-2xs">
                <Compass className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
              </div>
              <div className="text-xs">
                <span className="font-normal text-[#0d3f48] block text-sm group-hover:text-[#1b7481] transition-colors">Industrial Expertise</span>
                <span className="text-[#4e6569] font-light">Turnkey System Design</span>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-center gap-3.5 p-2 rounded-xl transition-all duration-300 hover:bg-white/40 hover:translate-x-1 group">
              <div className="w-10 h-10 bg-[#eef7f9] text-[#1b7481] flex items-center justify-center group-hover:bg-[#1b7481] group-hover:text-white transition-colors duration-300 shadow-2xs">
                <Award className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-xs">
                <span className="font-normal text-[#0d3f48] block text-sm group-hover:text-[#1b7481] transition-colors">{brand.isoCert || 'ISO 9001:2015'}</span>
                <span className="text-[#4e6569] font-light">Certified Engineering</span>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex items-center gap-3.5 p-2 rounded-xl transition-all duration-300 hover:bg-white/40 hover:translate-x-1 group">
              <div className="w-10 h-10 bg-[#eef7f9] text-[#1b7481] flex items-center justify-center group-hover:bg-[#1b7481] group-hover:text-white transition-colors duration-300 shadow-2xs">
                <Award className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-xs">
                <span className="font-normal text-[#0d3f48] block text-sm group-hover:text-[#1b7481] transition-colors">{brand.partnership || 'Siemens Partner'}</span>
                <span className="text-[#4e6569] font-light">Direct Hardware Supply</span>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex items-center gap-3.5 p-2 rounded-xl transition-all duration-300 hover:bg-white/40 hover:translate-x-1 group">
              <div className="w-10 h-10 bg-[#eef7f9] text-[#1b7481] flex items-center justify-center group-hover:bg-[#1b7481] group-hover:text-white transition-colors duration-300 shadow-2xs">
                <Globe2 className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
              </div>
              <div className="text-xs">
                <span className="font-normal text-[#0d3f48] block text-sm group-hover:text-[#1b7481] transition-colors">{brand.globalDestinations || '14+'} Countries</span>
                <span className="text-[#4e6569] font-light">International Footprint</span>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* Dynamic Benefit Cards (Configured from Admin Portal Home Page Section 03) */}
      <div
        className={`grid grid-cols-1 ${
          benefitCards.length === 2 ? 'md:grid-cols-2' : benefitCards.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'
        } gap-6 pt-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 delay-500 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {benefitCards.map((card, idx) => {
          const IconComp = getBenefitIcon(card.icon);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl border border-[#1b6f7e]/15 shadow-2xs hover:shadow-xl hover:-translate-y-2 hover:border-[#1b7481]/50 transition-all duration-300 flex flex-col justify-between group cursor-default"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#eef7f9] text-[#1b7481] flex items-center justify-center mb-4 group-hover:bg-[#1b7481] group-hover:text-white transition-colors duration-300 shadow-2xs">
                  <IconComp className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
                </div>
                <h3
                  className="text-lg font-normal mb-2 group-hover:text-[#1b7481] transition-colors"
                  style={{
                    fontFamily: cardTitleFontFamily,
                    fontSize: cardTitleFontSize || undefined,
                    color: cardTitleColor,
                  }}
                >
                  {card.title}
                </h3>
                <div
                  className="text-xs sm:text-sm font-light leading-relaxed prose prose-sm"
                  style={{
                    color: cardDescColor,
                  }}
                  dangerouslySetInnerHTML={{ __html: card.description }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
